import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header1 from "../components/default-header";
import DefaultFooter from "../components/default-footer";
import { API_URL } from "../config";

const Trips = () => {
  const [activities, setActivities] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        const response = await axios.get(`${API_URL}/activities`);
        const data = response.data.data?.data ? response.data.data.data : (response.data.data || response.data);
        setActivities(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching activities:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchActivities();
  }, []);

  // Bandeau éditorial en haut de page — 3 mises en avant, sobre et élégant
  const highlights = [
    {
      id: 1,
      tag: "Just Added",
      title: "New Turkey Tours Now Open",
      image: "https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?auto=format&fit=crop&w=800&q=85",
    },
    {
      id: 2,
      tag: "Trending",
      title: "Salalah's Khareef Season",
      image: "https://images.unsplash.com/photo-1544644181-1484b3fdfc62?auto=format&fit=crop&w=800&q=85",
    },
    {
      id: 3,
      tag: "Best Seller",
      title: "Island Hopping in Thailand",
      image: "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?auto=format&fit=crop&w=800&q=85",
    },
  ];

  // Nos voyages en Oman — le cœur de l'agence
  const featuredDestinations = [
    {
      id: 1,
      title: "Wahiba Sands Desert Safari",
      location: "Sharqiya Sands, Oman",
      duration: "2 Days / 1 Night",
      price: "75 OMR",
      image: "https://images.unsplash.com/photo-1509316975850-ff9c5deb0cd9?auto=format&fit=crop&w=900&q=85",
      tag: "Most Popular",
    },
    {
      id: 2,
      title: "Wadi Shab & Bimmah Sinkhole",
      location: "Tiwi, Oman",
      duration: "Full Day Trip",
      price: "35 OMR",
      image: "https://images.unsplash.com/photo-1544644181-1484b3fdfc62?auto=format&fit=crop&w=900&q=85",
      tag: "Adventure",
    },
    {
      id: 3,
      title: "Jebel Akhdar Mountain Escape",
      location: "Al Hajar Mountains",
      duration: "2 Days / 1 Night",
      price: "120 OMR",
      image: "https://images.unsplash.com/photo-1589308078059-be1415eab4c3?auto=format&fit=crop&w=900&q=85",
      tag: "Luxury & Nature",
    },
  ];

  // Top Destinations — carrousel, badge nombre de circuits + prix
  const topDestinations = [
    {
      id: 1,
      country: "Oman",
      title: "Wahiba Sands & Wadis",
      tours: 46,
      priceFrom: "75 OMR",
      image: "https://images.unsplash.com/photo-1509316975850-ff9c5deb0cd9?auto=format&fit=crop&w=800&q=85",
    },
    {
      id: 2,
      country: "Turkey",
      title: "Cappadocia & Istanbul",
      tours: 38,
      priceFrom: "349 OMR",
      image: "https://images.unsplash.com/photo-1641128324972-af3212f0f6bd?auto=format&fit=crop&w=800&q=85",
    },
    {
      id: 3,
      country: "United Arab Emirates",
      title: "Dubai & Abu Dhabi",
      tours: 61,
      priceFrom: "199 OMR",
      image: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?auto=format&fit=crop&w=800&q=85",
    },
    {
      id: 4,
      country: "Thailand",
      title: "Phuket & Phi Phi Islands",
      tours: 52,
      priceFrom: "429 OMR",
      image: "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?auto=format&fit=crop&w=800&q=85",
    },
    {
      id: 5,
      country: "Indonesia",
      title: "Bali Escape",
      tours: 44,
      priceFrom: "459 OMR",
      image: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?auto=format&fit=crop&w=800&q=85",
    },
    {
      id: 6,
      country: "Egypt",
      title: "Cairo & the Nile",
      tours: 29,
      priceFrom: "319 OMR",
      image: "https://images.unsplash.com/photo-1568322445389-f64ac2515020?auto=format&fit=crop&w=800&q=85",
    },
  ];

  const carouselRef = useRef<HTMLDivElement>(null);
  const isCarouselPaused = useRef(false);

  const scrollCarousel = (dir: "left" | "right") => {
    if (!carouselRef.current) return;
    const amount = carouselRef.current.clientWidth * 0.85;
    carouselRef.current.scrollBy({ left: dir === "left" ? -amount : amount, behavior: "smooth" });
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const el = carouselRef.current;
      if (!el || isCarouselPaused.current) return;
      const isNearEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 12;
      if (isNearEnd) {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        el.scrollBy({ left: 300, behavior: "smooth" });
      }
    }, 3200);
    return () => clearInterval(interval);
  }, []);

  const trustPoints = [
    { icon: "fa-map-location-dot", title: "Local Experts", text: "Based in Oman, with guides who know every route personally." },
    { icon: "fa-sack-dollar", title: "Fair, Clear Pricing", text: "No hidden fees — the price you see is the price you pay." },
    { icon: "fa-headset", title: "Support On The Ground", text: "A local contact reachable throughout your trip, not just before booking." },
    { icon: "fa-calendar-check", title: "Flexible Booking", text: "Free date changes up to 7 days before departure." },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');

        :root {
          --teal: #294b5a;
          --teal-d: #1a3140;
          --teal-l: #3b6272;
          --gold: #d3be76;
          --gold-l: #ede4b5;
          --gold-d: #b09a50;
          --cream: #f7f4ee;
          --mist: #edeae2;
          --silver: #8a9aa3;
          --ink: #1a2f38;
        }

        .trip-page { font-family: 'DM Sans', sans-serif; background: var(--cream); color: var(--ink); }
        .trip-page h1, .trip-page h2, .trip-page h3 { font-family: 'Playfair Display', serif; }

        .trip-hero { padding: 130px 24px 40px; text-align: center; }
        .trip-eyebrow {
          display: inline-flex; align-items: center; gap: 8px;
          font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase;
          color: var(--gold-d); background: rgba(211,190,118,.12);
          border: 1px solid rgba(211,190,118,.35); border-radius: 30px;
          padding: 7px 16px; margin-bottom: 16px;
        }
        .trip-title { font-size: clamp(1.7rem, 2.4vw, 2.35rem); font-weight: 600; color: var(--teal); margin: 0; }
        .trip-title em { color: var(--gold-d); font-style: italic; }

        .trip-container { max-width: 1200px; margin: 0 auto; padding: 0 20px; }
        .trip-section { margin-bottom: 88px; }
        .section-head { max-width: 640px; margin: 0 auto 36px; text-align: center; }
        .section-head.align-left { margin: 0 0 36px; text-align: left; }
        .section-title { font-size: 2rem; color: var(--teal); margin-bottom: 8px; font-weight: 600; }
        .section-desc { color: var(--silver); font-size: 14.5px; line-height: 1.7; }
        .section-toolbar {
          display: flex; justify-content: space-between; align-items: flex-end;
          flex-wrap: wrap; gap: 18px; margin-bottom: 32px;
        }
        .section-toolbar .section-title-row { display: flex; align-items: center; gap: 10px; }
        .section-toolbar .section-title-row i { color: var(--gold-d); font-size: 20px; }
        .toolbar-right { display: flex; align-items: center; gap: 14px; }
        .view-all-link { color: var(--teal); font-size: 13px; font-weight: 700; text-decoration: none; border-bottom: 1.5px solid var(--gold); padding-bottom: 2px; }
        .nav-btn {
          width: 40px; height: 40px; border-radius: 50%;
          border: 1.5px solid rgba(41,75,90,.18); background: #fff;
          display: flex; align-items: center; justify-content: center;
          color: var(--teal); cursor: pointer; transition: all .2s ease; font-size: 13px;
        }
        .nav-btn:hover { background: var(--teal); color: #fff; border-color: var(--teal); }

        .cards-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(340px, 1fr)); gap: 30px; }

        /* Highlights strip — editorial, minimal */
        .highlight-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        .highlight-card {
          position: relative; height: 300px; border-radius: 18px; overflow: hidden;
          box-shadow: 0 14px 30px -16px rgba(26,47,56,0.3);
        }
        .highlight-card img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .5s ease; }
        .highlight-card:hover img { transform: scale(1.06); }
        .highlight-card::after {
          content: ""; position: absolute; inset: 0;
          background: linear-gradient(180deg, rgba(19,35,42,0) 42%, rgba(15,28,34,.9) 100%);
        }
        .highlight-frame {
          position: absolute; inset: 14px; border: 1px solid rgba(255,255,255,.35);
          border-radius: 10px; z-index: 2; pointer-events: none;
        }
        .highlight-tag {
          position: absolute; top: 28px; left: 28px; z-index: 3;
          background: var(--gold); color: var(--teal-d); font-size: 10.5px; font-weight: 700;
          letter-spacing: .5px; text-transform: uppercase; padding: 5px 12px; border-radius: 20px;
        }
        .highlight-title {
          position: absolute; left: 28px; right: 28px; bottom: 26px; z-index: 3;
          color: #fff; font-family: 'DM Sans', sans-serif; font-weight: 700; font-size: 1.35rem; line-height: 1.3;
        }

        /* Trip package cards (Oman) */
        .trip-card {
          background: #ffffff; border-radius: 16px; overflow: hidden;
          border: 1px solid rgba(41,75,90,.08); box-shadow: 0 10px 30px -10px rgba(0,0,0,0.05);
          transition: transform .3s ease, box-shadow .3s ease; display: flex; flex-direction: column;
        }
        .trip-card:hover { transform: translateY(-6px); box-shadow: 0 20px 40px -15px rgba(41,75,90,0.12); }
        .card-img-wrapper { position: relative; height: 220px; overflow: hidden; }
        .card-img-wrapper img { width: 100%; height: 100%; object-fit: cover; transition: transform .5s ease; }
        .trip-card:hover .card-img-wrapper img { transform: scale(1.05); }
        .card-badge {
          position: absolute; top: 16px; left: 16px;
          background: rgba(26, 47, 56, 0.85); backdrop-filter: blur(4px);
          color: var(--gold); font-size: 11px; font-weight: 700; padding: 6px 12px; border-radius: 20px; letter-spacing: 0.5px;
        }
        .card-price {
          position: absolute; bottom: 16px; right: 16px;
          background: var(--gold); color: var(--teal-d); font-size: 13px; font-weight: 700;
          padding: 6px 14px; border-radius: 20px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        .card-body { padding: 24px; display: flex; flex-direction: column; flex: 1; }
        .card-meta { display: flex; align-items: center; gap: 12px; font-size: 12px; color: var(--silver); margin-bottom: 10px; font-weight: 500; }
        .card-title { font-size: 1.2rem; color: var(--teal); font-weight: 600; margin-bottom: 8px; }
        .card-text { font-size: 13.5px; color: var(--silver); line-height: 1.6; margin-bottom: 20px; flex: 1; }
        .card-btn {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          background: transparent; color: var(--teal); border: 1.5px solid var(--teal);
          border-radius: 8px; padding: 10px 18px; font-size: 13px; font-weight: 600;
          text-decoration: none; transition: all .2s ease; cursor: pointer;
        }
        .card-btn:hover { background: var(--teal); color: #fff; }

        /* Top Destinations carousel */
        .dest-track {
          display: flex; gap: 22px; overflow-x: auto; padding: 4px 4px 18px;
          scroll-snap-type: x mandatory; scrollbar-width: none;
        }
        .dest-track::-webkit-scrollbar { display: none; }
        .dest-card {
          position: relative; flex: 0 0 auto; width: 270px; height: 340px;
          border-radius: 18px; overflow: hidden; scroll-snap-align: start;
          text-decoration: none; display: block;
          box-shadow: 0 14px 30px -16px rgba(26,47,56,0.3);
        }
        .dest-card img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform .6s ease; }
        .dest-card:hover img { transform: scale(1.08); }
        .dest-card::after {
          content: ""; position: absolute; inset: 0;
          background: linear-gradient(180deg, rgba(19,35,42,.1) 0%, rgba(19,35,42,0) 30%, rgba(15,28,34,.92) 100%);
        }
        .dest-card-top { position: absolute; top: 22px; left: 22px; right: 22px; z-index: 2; }
        .dest-card-country { color: rgba(255,255,255,.8); font-size: 10.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; display: block; margin-bottom: 4px; }
        .dest-card-title { color: #fff; font-family: 'DM Sans', sans-serif; font-weight: 700; font-size: 1.2rem; line-height: 1.25; }
        .dest-card-bottom {
          position: absolute; left: 22px; bottom: 20px; z-index: 2;
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(255,255,255,.14); backdrop-filter: blur(6px);
          border: 1px solid rgba(255,255,255,.3); border-radius: 20px; padding: 6px 14px;
        }
        .dest-card-bottom span { color: #fff; font-size: 11.5px; font-weight: 600; }
        .dest-card-bottom strong { color: var(--gold); }
        .dest-card-arrow {
          position: absolute; right: 18px; bottom: 18px; z-index: 2;
          width: 32px; height: 32px; border-radius: 50%;
          background: var(--gold); color: var(--teal-d);
          display: flex; align-items: center; justify-content: center; font-size: 12px;
          opacity: 0; transform: translateY(6px); transition: all .25s ease;
        }
        .dest-card:hover .dest-card-arrow { opacity: 1; transform: translateY(0); }

        @media (max-width: 640px) {
          .highlight-grid { grid-template-columns: 1fr; }
          .dest-card { width: 220px; height: 300px; }
        }

        /* Trust strip */
        .trust-strip {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 28px;
          background: #ffffff; border: 1px solid rgba(41,75,90,.08); border-radius: 18px; padding: 40px;
        }
        .trust-item { display: flex; flex-direction: column; gap: 10px; }
        .trust-icon {
          width: 44px; height: 44px; border-radius: 12px; background: rgba(211,190,118,.16); color: var(--gold-d);
          display: flex; align-items: center; justify-content: center; font-size: 18px;
        }
        .trust-item h4 { font-family: 'Playfair Display', serif; font-size: 1.05rem; color: var(--teal); margin: 0; }
        .trust-item p { font-size: 13px; color: var(--silver); line-height: 1.6; margin: 0; }

        /* CTA Banner */
        .cta-banner {
          background: linear-gradient(135deg, var(--teal-d), var(--teal));
          border-radius: 20px; padding: 60px 40px; text-align: center; color: #fff;
          box-shadow: 0 20px 40px rgba(41,75,90,0.2);
        }
        .cta-banner h2 { color: #fff; margin-bottom: 15px; font-size: 2.2rem; }
        .cta-banner p { color: var(--gold-l); font-size: 15px; max-width: 550px; margin: 0 auto 30px; }
        .btn-gold {
          background: var(--gold); color: var(--teal-d); font-weight: 700;
          padding: 14px 28px; border-radius: 8px; text-decoration: none; display: inline-flex;
          align-items: center; gap: 8px; transition: transform .2s, background .2s;
          box-shadow: 0 8px 20px rgba(211,190,118,0.3);
        }
        .btn-gold:hover { background: #e2ce86; transform: translateY(-2px); }
      `}</style>

      <div className="trip-page">
        <Header1 />

        {/* HERO */}
        <section className="trip-hero">
          <div className="trip-eyebrow">Oman Travel & Experiences</div>
          <h1 className="trip-title">
            Discover the Magic of <em>Oman</em>
          </h1>
        </section>

        <div className="trip-container" style={{ marginTop: "10px" }}>

          {/* SECTION 1: HIGHLIGHTS STRIP */}
          <div className="trip-section">
            <div className="highlight-grid">
              {highlights.map((h) => (
                <div className="highlight-card" key={h.id}>
                  <img src={h.image} alt={h.title} />
                  <div className="highlight-frame"></div>
                  <span className="highlight-tag">{h.tag}</span>
                  <div className="highlight-title">{h.title}</div>
                </div>
              ))}
            </div>
          </div>

          {/* SECTION 2: FEATURED OMAN TRIPS */}
          <div className="trip-section">
            <div className="section-head align-left">
              <h2 className="section-title">Featured Destinations & Trips</h2>
              <p className="section-desc">Hand-crafted travel packages to explore the most breathtaking landscapes of Oman.</p>
            </div>

            <div className="cards-grid">
              {featuredDestinations.map((trip) => (
                <div className="trip-card" key={trip.id}>
                  <div className="card-img-wrapper">
                    <span className="card-badge">{trip.tag}</span>
                    <img src={trip.image} alt={trip.title} />
                    <span className="card-price">{trip.price}</span>
                  </div>
                  <div className="card-body">
                    <div className="card-meta">
                      <span><i className="fa-solid fa-location-dot me-1" style={{ color: "var(--gold-d)" }}></i> {trip.location}</span>
                      <span>•</span>
                      <span><i className="fa-regular fa-clock me-1"></i> {trip.duration}</span>
                    </div>
                    <h3 className="card-title">{trip.title}</h3>
                    <p className="card-text">Experience the ultimate guided excursion with top-tier comfort, expert local guides, and unique scenery.</p>
                    <Link to="/contact" className="card-btn">
                      Book This Trip <i className="fa-solid fa-arrow-right"></i>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* SECTION 3: TOP DESTINATIONS — CAROUSEL */}
          <div className="trip-section">
            <div className="section-toolbar">
              <div className="section-title-row">
                <i className="fa-solid fa-compass"></i>
                <div>
                  <h2 className="section-title" style={{ margin: 0 }}>Top Destinations</h2>
                  <p className="section-desc" style={{ margin: "4px 0 0 0" }}>Where our travelers go most, straight from Muscat.</p>
                </div>
              </div>
              <div className="toolbar-right">
                <Link to="/contact" className="view-all-link">View All Destinations</Link>
                <button className="nav-btn" onClick={() => scrollCarousel("left")} aria-label="Scroll left">
                  <i className="fa-solid fa-chevron-left"></i>
                </button>
                <button className="nav-btn" onClick={() => scrollCarousel("right")} aria-label="Scroll right">
                  <i className="fa-solid fa-chevron-right"></i>
                </button>
              </div>
            </div>

            <div
              className="dest-track"
              ref={carouselRef}
              onMouseEnter={() => (isCarouselPaused.current = true)}
              onMouseLeave={() => (isCarouselPaused.current = false)}
              onTouchStart={() => (isCarouselPaused.current = true)}
              onTouchEnd={() => (isCarouselPaused.current = false)}
            >
              {topDestinations.map((dest) => (
                <Link to="/contact" className="dest-card" key={dest.id}>
                  <img src={dest.image} alt={`${dest.title} — ${dest.country}`} loading="lazy" />
                  <div className="dest-card-top">
                    <span className="dest-card-country">{dest.country}</span>
                    <div className="dest-card-title">{dest.title}</div>
                  </div>
                  <div className="dest-card-bottom">
                    <span>{dest.tours} Tours · From <strong>{dest.priceFrom}</strong></span>
                  </div>
                  <span className="dest-card-arrow"><i className="fa-solid fa-arrow-right"></i></span>
                </Link>
              ))}
            </div>
          </div>

          {/* SECTION 4: WHY TRAVEL WITH US */}
          <div className="trip-section">
            <div className="section-head">
              <h2 className="section-title">Why Travel With Us</h2>
              <p className="section-desc">A small, Oman-based team that plans every trip personally — not a booking engine.</p>
            </div>
            <div className="trust-strip">
              {trustPoints.map((point) => (
                <div className="trust-item" key={point.title}>
                  <div className="trust-icon"><i className={`fa-solid ${point.icon}`}></i></div>
                  <h4>{point.title}</h4>
                  <p>{point.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* SECTION 5: ACTIVITIES & EXCURSIONS (Dynamique depuis l'API) */}
          <div className="trip-section">
            <div className="section-head align-left">
              <h2 className="section-title">Activities & Leisure</h2>
              <p className="section-desc">Daily excursions, water sports, and guided local experiences.</p>
            </div>

            {isLoading ? (
              <div style={{ textAlign: "center", padding: "40px", color: "var(--silver)" }}>
                <i className="fa-solid fa-circle-notch fa-spin fa-2x" style={{ color: "var(--teal)" }}></i>
              </div>
            ) : activities.length > 0 ? (
              <div className="cards-grid">
                {activities.map((act) => (
                  <div className="trip-card" key={act.id}>
                    <div className="card-img-wrapper" style={{ background: "var(--mist)" }}>
                      {act.photos && act.photos.length > 0 ? (
                        <img src={act.photos[0].path} alt={act.name} />
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "var(--teal)" }}>
                          <i className="fa-solid fa-compass fa-3x" style={{ opacity: 0.4 }}></i>
                        </div>
                      )}
                      <span className="card-price">{act.price} OMR</span>
                    </div>
                    <div className="card-body">
                      <h3 className="card-title">{act.name}</h3>
                      <p className="card-text">{act.description ? `${act.description.substring(0, 90)}...` : "Enjoy an amazing guided activity tailored to your schedule."}</p>
                      <Link to="/contact" className="card-btn">
                        Explore Activity <i className="fa-solid fa-arrow-right"></i>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: "center", padding: "40px", background: "#fff", borderRadius: "12px", border: "1px solid rgba(41,75,90,0.1)" }}>
                <p style={{ color: "var(--silver)", margin: 0 }}>No activities available right now. Check back soon or contact us for custom bookings!</p>
              </div>
            )}
          </div>

          {/* CTA BANNER */}
          <div className="cta-banner">
            <h2>Ready for Your Custom Journey?</h2>
            <p>Let our local travel designers build a personalized itinerary tailored exactly to your preferences, dates, and budget.</p>
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <Link to="/contact" className="btn-gold">
                Plan Custom Trip <i className="fa-solid fa-sparkles"></i>
              </Link>
              <a href="https://wa.me/" target="_blank" rel="noreferrer" style={{ background: "transparent", color: "#fff", border: "1.5px solid rgba(255,255,255,0.3)", borderRadius: "8px", padding: "14px 24px", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "8px" }}>
                <i className="fa-brands fa-whatsapp" style={{ fontSize: "18px", color: "#25D366" }}></i> Chat on WhatsApp
              </a>
            </div>
          </div>

        </div>

        <DefaultFooter />
      </div>
    </>
  );
};

export default Trips;